/**
 * /dashboard/ai-writer — Premium long-form AI Writer (Claude Sonnet 4.5)
 *
 * Members on the $49 Pro and $79 Unlimited tiers (during the 7-day trial included) can use
 * this. Mike calls it "AI Writer." Different from AI Writer — generates
 * 1500-2500 word articles, chapters, devotionals, and apologetics essays with:
 *   - reading level toggle (6th / 8th / college)
 *   - writing style toggle (paragraphs vs short-line)
 *   - reference book(s) — Builder=5, Unlimited=unlimited
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, ArrowLeft, BookOpen, Plus, Trash2, Copy, Download,
  Lock, AlertCircle, Crown, FileText, ChevronRight,
} from "lucide-react";

const O    = "#F39A1F";
const WINE = "#0F172A";
const G    = "#2E9F3A";

const MODES = [
  { id: "article",     label: "Article",     words: 1500, note: "Magazine-style article — story-driven, 1500 words" },
  { id: "chapter",     label: "Book Chapter", words: 2500, note: "Long-form chapter — 2500 words with hook, points, transition" },
  { id: "devotional",  label: "Devotional",   words: 800,  note: "Daily devotional — 800 words with verse + lesson + prayer" },
  { id: "apologetics", label: "Apologetics",  words: 2000, note: "Defending the faith — 2000-word essay with sources" },
];

const READING_LEVELS = [
  { id: "5th",     label: "5th Grade",     note: "Simple words. The way most folks talk." },
  { id: "6th",     label: "6th Grade",     note: "Easy reading. Recommended default." },
  { id: "8th",     label: "8th Grade",     note: "Bigger words. Use only if needed." },
];

const STYLES = [
  { id: "paragraphs",  label: "Flowing Paragraphs", note: "Sermon-style. Story-driven." },
  { id: "short_line",  label: "Short Punchy Lines", note: "Bullet-heavy. White space. Social media." },
];

export default function AIWriterPage() {
  const [usage, setUsage] = useState(null);
  const [topic, setTopic] = useState("");
  const [mode, setMode] = useState("article");
  const [level, setLevel] = useState("6th");
  const [style, setStyle] = useState("paragraphs");
  const [words, setWords] = useState(1500);
  const [refs, setRefs] = useState([{ title: "", author: "", notes: "" }]);
  const [extra, setExtra] = useState("");
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);  // seconds since generate started — shown in loading state
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadUsage = async () => {
    try {
      const { data } = await api.get("/ai-writer/usage");
      setUsage(data);
    } catch {
      setUsage({ allowed: false });
    }
  };
  const loadHistory = async () => {
    try {
      const { data } = await api.get("/ai-writer/history", { params: { limit: 12 } });
      setHistory(data.items || []);
    } catch {}
  };

  useEffect(() => { loadUsage(); loadHistory(); }, []);
  useEffect(() => {
    const m = MODES.find((x) => x.id === mode);
    if (m) setWords(m.words);
  }, [mode]);

  const refLimit = usage?.ref_limit_per_article || 0;

  const updateRef = (i, field, val) => {
    setRefs((r) => r.map((x, idx) => (idx === i ? { ...x, [field]: val } : x)));
  };
  const addRef = () => {
    if (refs.length >= refLimit) {
      toast.error(`Your tier allows ${refLimit} reference book(s).`);
      return;
    }
    setRefs((r) => [...r, { title: "", author: "", notes: "" }]);
  };
  const removeRef = (i) => setRefs((r) => r.filter((_, idx) => idx !== i));

  const generate = async () => {
    if (!topic.trim()) {
      toast.error("Type what you want an article about.");
      return;
    }
    setLoading(true);
    setElapsed(0);
    setResult(null);
    // Start a 1-second ticker so the user sees the seconds counting up. Claude
    // takes roughly 30-90s for full-length pieces; without a counter it FEELS
    // like the page is hung. Cleared in finally{}.
    const tickStart = Date.now();
    const tickId = setInterval(() => setElapsed(Math.floor((Date.now() - tickStart) / 1000)), 1000);
    try {
      const cleanRefs = refs.filter((r) => r.title.trim()).slice(0, refLimit);
      const { data } = await api.post("/ai-writer/generate", {
        topic: topic.trim(),
        mode, reading_level: level, style,
        target_words: Number(words),
        reference_books: cleanRefs,
        extra_instructions: extra.trim(),
      }, { timeout: 240000 });  // 4-min ceiling so axios doesn't bail before Claude finishes
      setResult(data);
      await loadUsage();
      await loadHistory();
      toast.success(`Article ready — ${data.word_count} words`);
    } catch (err) {
      const msg = err?.code === "ECONNABORTED"
        ? "Took longer than 4 minutes. Try a shorter piece (1500 words) and run it again."
        : (err?.response?.data?.detail || err?.message || "Generation failed");
      toast.error(msg);
    } finally {
      clearInterval(tickId);
      setLoading(false);
    }
  };

  const copyResult = () => {
    if (!result?.result) return;
    navigator.clipboard.writeText(result.result);
    toast.success("Copied to clipboard");
  };
  const downloadResult = () => {
    if (!result?.result) return;
    const blob = new Blob([result.result], { type: "text/markdown;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ai-writer-${result.id || Date.now()}.md`;
    link.click();
  };

  // ───── tier gate ─────
  if (usage && !usage.allowed) {
    return (
      <div className="min-h-screen pb-16" style={{ background: "#FFFFFF" }} data-testid="ai-writer-locked">
        <div className="max-w-3xl mx-auto px-5 md:px-8 pt-10">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider-cap font-bold text-foreground/55 hover:text-red-wine mb-6">
            <ArrowLeft className="w-3.5 h-3.5"/> Back to My Office
          </Link>
          <div className="rounded-2xl bg-white shadow-2xl p-8 text-center" style={{ border: `3px solid ${O}` }}>
            <Lock className="w-12 h-12 mx-auto" style={{ color: O }}/>
            <h1 className="font-serif-display text-3xl md:text-4xl uppercase mt-3" style={{ color: WINE }}>AI Writer Is A Paid-Tier Tool</h1>
            <p className="text-base mt-4 max-w-xl mx-auto leading-relaxed">
              Mike built <strong>AI Writer</strong> for serious authors — full magazine articles, book chapters, apologetics essays. It runs on <strong>Claude Sonnet 4.5</strong> (the gold standard) and understands reference books like Dr. Frank Turek's <em>Stealing From God</em>.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-xl mx-auto text-left">
              <div className="rounded-xl bg-white-soft border-2 p-4" style={{ borderColor: `${G}55` }}>
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: G }}>★ MINISTRY BUILDER</div>
                <div className="font-serif-display text-xl mt-1" style={{ color: WINE }}>$49<span className="text-sm text-foreground/60">/mo</span></div>
                <div className="text-xs text-foreground/80 mt-1">20 articles/day · 5 reference books per article</div>
              </div>
              <div className="rounded-xl p-4 text-white" style={{ background: `linear-gradient(135deg, ${WINE}, #7C2A2A)`, border: `2px solid ${O}` }}>
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: O }}>★ KINGDOM BUILDER UNLIMITED</div>
                <div className="font-serif-display text-xl mt-1" style={{ color: "#E0F2FE" }}>$79<span className="text-sm text-white/60">/mo</span></div>
                <div className="text-xs text-white/85 mt-1">60 articles/day · unlimited reference books · max email</div>
              </div>
            </div>
            <Link to="/pricing" data-testid="ai-writer-upgrade"
                  className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
                  style={{ background: O }}>
              <Crown className="w-4 h-4"/> See Pricing &amp; Start 7-Day Trial
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" style={{ background: "#FFFFFF" }} data-testid="ai-writer-page">
      <div className="max-w-6xl mx-auto px-5 md:px-8 pt-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider-cap font-bold text-foreground/55 hover:text-red-wine mb-4">
          <ArrowLeft className="w-3.5 h-3.5"/> Back to Dashboard
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>★ FAITHARAMA · PREMIUM LONG-FORM ENGINE</div>
            <h1 className="font-serif-display text-4xl md:text-5xl uppercase mt-1 leading-[1.05]" style={{ color: WINE }}>
              <Sparkles className="inline w-7 h-7 mb-2" style={{ color: O }}/> AI Writer
            </h1>
            <p className="text-sm text-foreground/70 mt-1">Powered by Claude Sonnet 4.6 — Magazine articles, full chapters, devotionals, apologetics essays.</p>
          </div>
          {usage && (
            <div className="rounded-xl bg-white border-2 px-4 py-3 text-right shadow-md" style={{ borderColor: `${O}55` }} data-testid="ai-writer-usage">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-foreground/60">Today</div>
              <div className="font-serif-display text-2xl" style={{ color: WINE }}>{usage.remaining_today}<span className="text-sm text-foreground/55"> / {usage.daily_limit}</span></div>
              <div className="text-[10px] text-foreground/60">articles left</div>
            </div>
          )}
        </div>

        {/* ─── CHAT-STYLE WRITER PROMO BAR ─── */}
        <Link to="/dashboard/writer-chat"
              data-testid="open-writer-chat-btn"
              className="block mt-5 rounded-2xl border-2 p-4 md:p-5 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition"
              style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 100%)", borderColor: O }}>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                 style={{ background: WINE, color: O }}>
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>
                ★ NEW · Chat With Our AI Writer
              </div>
              <div className="font-serif-display text-xl md:text-2xl uppercase leading-tight" style={{ color: WINE }}>
                AI Writer Chat
              </div>
              <div className="text-sm" style={{ color: "#3D3D3D" }}>
                Talk back and forth like ChatGPT — rewrite drafts, get headlines, draft emails, all in plain Southern voice.
              </div>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider shadow"
                 style={{ background: O, color: WINE, border: `2px solid ${WINE}` }}>
              Open Chat <ChevronRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </Link>

        <div className="grid lg:grid-cols-[1fr_400px] gap-6 mt-6">
          {/* ─── FORM ─── */}
          <div className="rounded-2xl bg-white p-5 md:p-6 shadow-md" style={{ border: `2px solid ${O}33` }}>
            <label className="block text-xs uppercase tracking-wider-cap font-bold text-foreground/70 mb-1.5">What's the article about?</label>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={3}
              placeholder={`e.g. "How God's grace meets us in our weakness" — be as specific as you want`}
              className="w-full rounded-md border-2 px-3 py-2.5 text-base focus:outline-none focus:border-red-wine"
              style={{ borderColor: "#E5DCC4" }}
              data-testid="ai-writer-topic"
            />

            <div className="grid md:grid-cols-2 gap-4 mt-5">
              <div>
                <label className="block text-xs uppercase tracking-wider-cap font-bold text-foreground/70 mb-1.5">Type of writing</label>
                <div className="grid grid-cols-2 gap-2">
                  {MODES.map((m) => (
                    <button key={m.id} type="button" onClick={() => setMode(m.id)}
                            data-testid={`ai-writer-mode-${m.id}`}
                            className={`rounded-md border-2 px-3 py-2 text-xs font-bold uppercase tracking-wider-cap text-left transition ${mode === m.id ? "text-white" : "bg-white text-foreground hover:border-red-wine"}`}
                            style={{ background: mode === m.id ? WINE : undefined, borderColor: mode === m.id ? WINE : "#E5DCC4" }}>
                      {m.label}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-foreground/60 mt-1.5 italic">{MODES.find((x) => x.id === mode)?.note}</div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider-cap font-bold text-foreground/70 mb-1.5">Target word count</label>
                <input type="range" min="600" max="3000" step="100" value={words} onChange={(e) => setWords(e.target.value)}
                       className="w-full" data-testid="ai-writer-words"/>
                <div className="text-sm font-bold mt-1" style={{ color: WINE }}>{words} words</div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-5">
              <div>
                <label className="block text-xs uppercase tracking-wider-cap font-bold text-foreground/70 mb-1.5">Reading level</label>
                <div className="grid grid-cols-3 gap-2">
                  {READING_LEVELS.map((r) => (
                    <button key={r.id} type="button" onClick={() => setLevel(r.id)}
                            data-testid={`ai-writer-level-${r.id}`}
                            className={`rounded-md border-2 px-2 py-2 text-xs font-bold uppercase tracking-wider-cap transition ${level === r.id ? "text-white" : "bg-white text-foreground hover:border-red-wine"}`}
                            style={{ background: level === r.id ? G : undefined, borderColor: level === r.id ? G : "#E5DCC4" }}>
                      {r.label}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-foreground/60 mt-1.5 italic">{READING_LEVELS.find((x) => x.id === level)?.note}</div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider-cap font-bold text-foreground/70 mb-1.5">Writing style</label>
                <div className="grid grid-cols-2 gap-2">
                  {STYLES.map((s) => (
                    <button key={s.id} type="button" onClick={() => setStyle(s.id)}
                            data-testid={`ai-writer-style-${s.id}`}
                            className={`rounded-md border-2 px-3 py-2 text-xs font-bold uppercase tracking-wider-cap transition ${style === s.id ? "text-white" : "bg-white text-foreground hover:border-red-wine"}`}
                            style={{ background: style === s.id ? O : undefined, borderColor: style === s.id ? O : "#E5DCC4", color: style === s.id ? "#0F172A" : undefined }}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-foreground/60 mt-1.5 italic">{STYLES.find((x) => x.id === style)?.note}</div>
              </div>
            </div>

            {/* REFERENCE BOOKS */}
            <div className="mt-5 rounded-xl p-4" style={{ background: "#EAF6FC", border: `2px solid ${O}55` }}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-xs uppercase tracking-wider-cap font-bold" style={{ color: WINE }}>
                    <BookOpen className="inline w-4 h-4 mr-1"/> Reference Books <span className="text-[10px] text-foreground/60">({refs.filter((r) => r.title).length} / {refLimit === 999 ? "∞" : refLimit})</span>
                  </div>
                  <div className="text-[11px] text-foreground/65 italic mt-0.5">e.g. "Stealing From God" by Frank Turek — AI Writer will use this as the foundation</div>
                </div>
                <button type="button" onClick={addRef} data-testid="ai-writer-add-ref"
                        className="text-xs font-bold uppercase tracking-wider-cap inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-white"
                        style={{ background: O, color: "#0F172A" }}>
                  <Plus className="w-3.5 h-3.5"/> Add Book
                </button>
              </div>
              <div className="mt-3 space-y-2.5">
                {refs.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2" data-testid={`ai-writer-ref-${i}`}>
                    <input value={r.title} onChange={(e) => updateRef(i, "title", e.target.value)} placeholder="Book Title" className="rounded-md border-2 px-2.5 py-1.5 text-sm bg-white" style={{ borderColor: "#E5DCC4" }}/>
                    <input value={r.author} onChange={(e) => updateRef(i, "author", e.target.value)} placeholder="Author (optional)" className="rounded-md border-2 px-2.5 py-1.5 text-sm bg-white" style={{ borderColor: "#E5DCC4" }}/>
                    <button type="button" onClick={() => removeRef(i)} className="rounded-md border-2 px-2 hover:bg-red-50" style={{ borderColor: "#E5DCC4" }} title="Remove">
                      <Trash2 className="w-4 h-4 text-red-wine"/>
                    </button>
                    {r.title && (
                      <input value={r.notes} onChange={(e) => updateRef(i, "notes", e.target.value)} placeholder="Optional: how to use this book (e.g. 'lean on the CRIMES framework')" className="col-span-3 rounded-md border-2 px-2.5 py-1.5 text-sm bg-white" style={{ borderColor: "#E5DCC4" }}/>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <label className="block text-xs uppercase tracking-wider-cap font-bold text-foreground/70 mb-1.5">Extra directions <span className="text-foreground/45">(optional)</span></label>
              <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder='e.g. "include 1 quote from Hebrews" — keep it short'
                     className="w-full rounded-md border-2 px-3 py-2 text-sm" style={{ borderColor: "#E5DCC4" }}/>
            </div>

            <button type="button" onClick={generate} disabled={loading}
                    data-testid="ai-writer-generate"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg disabled:opacity-60 hover:scale-[1.01] transition"
                    style={{ background: WINE }}>
              {loading
                ? <><Loader2 className="w-5 h-5 animate-spin"/> Writing… {elapsed}s</>
                : <><Sparkles className="w-5 h-5"/> Generate Article</>}
            </button>

            {loading && (
              <div className="mt-3 rounded-md px-4 py-3 text-sm flex items-start gap-2 animate-pulse" data-testid="ai-writer-wait-notice"
                   style={{ background: "#FFFFFF", border: `1px solid ${O}55`, color: "#3A2F23" }}>
                <Loader2 className="w-4 h-4 mt-0.5 flex-shrink-0 animate-spin" style={{ color: WINE }}/>
                <div>
                  <strong>Hang tight — this can take a minute or two.</strong> Full-length pieces (1,500–3,000 words) usually finish in 60–90 seconds. Don't refresh the page. The article will pop up when it's ready.
                </div>
              </div>
            )}
            {usage && usage.remaining_today === 0 && (
              <div className="mt-3 rounded-md px-3 py-2 text-sm flex items-start gap-2" style={{ background: "#FFE9C5", border: `1px solid ${O}`, color: WINE }}>
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                <span>You've used all <strong>{usage.daily_limit}</strong> AI Writer articles for today. Resets at midnight UTC.</span>
              </div>
            )}
          </div>

          {/* ─── HISTORY SIDEBAR ─── */}
          <div className="rounded-2xl bg-white p-5 shadow-md" style={{ border: `2px solid ${G}33` }}>
            <div className="text-xs uppercase tracking-wider-cap font-bold text-foreground/70 flex items-center gap-1.5">
              <FileText className="w-4 h-4"/> Recent Articles
            </div>
            <div className="text-[11px] text-foreground/55 mt-1 mb-3 italic">Click any green card below to re-open the full article.</div>
            {history.length === 0 ? (
              <div className="text-sm text-foreground/55 italic mt-3 leading-relaxed">Your articles will show up here. Each one's saved — come back anytime.</div>
            ) : (
              <ul className="mt-3 space-y-2 max-h-[720px] overflow-y-auto">
                {history.map((h) => (
                  <li key={h.id} className="rounded-md border-2 px-3 py-3 cursor-pointer transition group hover:shadow-md"
                      style={{ borderColor: `${G}66`, background: "#FFFFFF" }}
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/ai-writer/article/${h.id}`);
                          setResult({
                            id: data.id, result: data.result, word_count: data.actual_words,
                            mode: data.mode, target_words: data.target_words,
                          });
                          window.scrollTo({ top: 0, behavior: "smooth" });
                          toast.success("Article opened below");
                        } catch {
                          toast.error("Could not load that article.");
                        }
                      }}
                      data-testid={`ai-writer-history-${h.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold leading-tight line-clamp-2" style={{ color: WINE }}>{h.topic}</div>
                        <div className="text-[11px] text-foreground/60 mt-0.5">{h.mode} · {h.actual_words} words · {h.reading_level} grade</div>
                      </div>
                      <span className="text-[10px] tracking-[0.18em] uppercase font-bold whitespace-nowrap px-2.5 py-1.5 rounded text-white group-hover:scale-105 transition shadow-md" style={{ background: G }}>
                        Open →
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* ─── RESULT ─── */}
        {result && (
          <div className="mt-8 rounded-2xl bg-white p-6 md:p-8 shadow-2xl" style={{ border: `3px solid ${WINE}` }} data-testid="ai-writer-result">
            <div className="flex items-center justify-between flex-wrap gap-3 pb-4 mb-4 border-b" style={{ borderColor: "#E5DCC4" }}>
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: O }}>★ AI WRITER · {result.mode}</div>
                <div className="text-sm text-foreground/60">{result.word_count} words (target was {result.target_words})</div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={copyResult} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wider-cap border-2 hover:bg-white-soft" style={{ borderColor: "#E5DCC4", color: WINE }} data-testid="ai-writer-copy">
                  <Copy className="w-3.5 h-3.5"/> Copy
                </button>
                <button type="button" onClick={downloadResult} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wider-cap text-white" style={{ background: WINE }} data-testid="ai-writer-download">
                  <Download className="w-3.5 h-3.5"/> Download .md
                </button>
              </div>
            </div>
            <article className="prose prose-lg max-w-none whitespace-pre-wrap leading-relaxed" style={{ color: "#0F172A" }}>
              {result.result}
            </article>
          </div>
        )}
      </div>
    </div>
  );
}
